import { type Dispatch, type SetStateAction, useCallback } from 'react';
import type { RepeatRowsPayload } from '@/components/custom/arito';

export const cloneDeepValue = (value: any): any => {
  if (value === null || typeof value !== 'object') return value;
  if (value instanceof Date) return new Date(value.getTime());

  if (Array.isArray(value)) {
    return value.map(item => cloneDeepValue(item));
  }

  return Object.keys(value).reduce((acc, key) => {
    acc[key] = cloneDeepValue((value as any)[key]);
    return acc;
  }, {} as any);
};

export const detailColumnDependencies: Record<string, string[]> = {
  ma_vt: ['ten_vt', 'dvt', 'dvt_data'],
  ma_kho: ['ma_kho_data', 'ma_bp_data']
};

const createTempRowUuid = (index: number) => `temp-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;

export interface ReplaceRowWithRowBatchOptions<TRow extends { uuid?: string | null; line?: number }> {
  anchorRowUuid: string;
  nextRows: Partial<TRow>[];
  mergeWithAnchor?: boolean;
}

export interface ReplaceRowWithRowBatchResult<TRow extends { uuid?: string | null; line?: number }> {
  rows: TRow[];
  insertedRowUuids: string[];
}

export const replaceRowWithRowBatch = <TRow extends { uuid?: string | null; line?: number }>(
  prevRows: TRow[],
  { anchorRowUuid, nextRows, mergeWithAnchor = false }: ReplaceRowWithRowBatchOptions<TRow>
): ReplaceRowWithRowBatchResult<TRow> => {
  const anchorIndex = prevRows.findIndex(row => row?.uuid === anchorRowUuid);

  if (anchorIndex === -1 || nextRows.length === 0) {
    return { rows: prevRows, insertedRowUuids: [] };
  }

  const anchorRow = prevRows[anchorIndex];
  const resolvedInsertedRows = nextRows.map((nextRow, index) => {
    const baseRow = mergeWithAnchor ? cloneDeepValue(anchorRow) : ({} as Partial<TRow>);
    const nextRowHasOwnUuid = Object.prototype.hasOwnProperty.call(nextRow, 'uuid');
    const resolvedRow = {
      ...baseRow,
      ...cloneDeepValue(nextRow)
    } as TRow;

    const inheritedAnchorUuid = mergeWithAnchor ? anchorRow?.uuid : undefined;

    if (!nextRowHasOwnUuid || !resolvedRow.uuid || resolvedRow.uuid === inheritedAnchorUuid) {
      resolvedRow.uuid = createTempRowUuid(index) as TRow['uuid'];
    }

    return resolvedRow;
  });

  const rows = [...prevRows.slice(0, anchorIndex), ...resolvedInsertedRows, ...prevRows.slice(anchorIndex + 1)].map(
    (row, index) =>
      ({
        ...row,
        line: index + 1
      }) as TRow
  );

  return {
    rows,
    insertedRowUuids: resolvedInsertedRows.map(row => String(row.uuid)).filter(Boolean)
  };
};

export interface ResolveSelectedItemsToRowsOptions<TItem, TRow> {
  items: TItem[];
  resolveRow: (item: TItem, index: number) => Promise<TRow | null | undefined>;
}

export const resolveSelectedItemsToRows = async <TItem, TRow>({
  items,
  resolveRow
}: ResolveSelectedItemsToRowsOptions<TItem, TRow>) => {
  const resolvedRows = await Promise.all(items.map((item, index) => resolveRow(item, index)));

  return resolvedRows.filter(row => row != null) as TRow[];
};

export interface ReplaceRowWithFieldValueBatchOptions<
  TRow extends { uuid?: string | null; line?: number },
  TField extends string,
  TValue
> {
  anchorRowUuid: string;
  field: TField;
  values: TValue[];
  createFields: (anchorRow: TRow, field: TField, value: TValue, index: number) => Partial<TRow>;
  normalizeRow?: (row: TRow, index: number) => TRow;
}

export const replaceRowWithFieldValueBatch = <
  TRow extends { uuid?: string | null; line?: number },
  TField extends string,
  TValue
>(
  prevRows: TRow[],
  {
    anchorRowUuid,
    field,
    values,
    createFields,
    normalizeRow
  }: ReplaceRowWithFieldValueBatchOptions<TRow, TField, TValue>
): ReplaceRowWithRowBatchResult<TRow> => {
  if (values.length === 0) {
    return { rows: prevRows, insertedRowUuids: [] };
  }

  const anchorRow = prevRows.find(row => row?.uuid === anchorRowUuid);

  if (!anchorRow) {
    return { rows: prevRows, insertedRowUuids: [] };
  }

  const nextRows = values.map((value, index) => createFields(anchorRow, field, value, index));
  const result = replaceRowWithRowBatch(prevRows, {
    anchorRowUuid,
    nextRows,
    mergeWithAnchor: true
  });

  return {
    ...result,
    rows: normalizeRow ? result.rows.map(normalizeRow) : result.rows
  };
};

export interface CreateResolvedFieldSelectionHandlerOptions<TItem, TValue> {
  field: string;
  resolveValue: (item: TItem, index: number) => Promise<TValue | null | undefined>;
  onSingleValue: (anchorRowUuid: string, field: string, value: TValue) => void | Promise<void>;
  onMultipleValues: (anchorRowUuid: string, field: string, values: TValue[]) => void | Promise<void>;
}

export const createResolvedFieldSelectionHandler =
  <TItem, TValue>({
    field,
    resolveValue,
    onSingleValue,
    onMultipleValues
  }: CreateResolvedFieldSelectionHandlerOptions<TItem, TValue>) =>
  async (anchorRowUuid: string, items: TItem[]) => {
    if (items.length === 0) {
      return;
    }

    const resolvedValues = await resolveSelectedItemsToRows({
      items,
      resolveRow: resolveValue
    });

    if (resolvedValues.length === 0) {
      return;
    }

    if (resolvedValues.length === 1) {
      await onSingleValue(anchorRowUuid, field, resolvedValues[0]);
      return;
    }

    await onMultipleValues(anchorRowUuid, field, resolvedValues);
  };

interface UseRepeatRowsHandlerOptions<T> {
  setRows: Dispatch<SetStateAction<T[]>>;
  markDirty?: () => void;
  columnDependencies?: Record<string, string[]>;
  onAfterRepeat?: (updatedRows: T[], repeatedField: string, sourceRow: T) => T[];
}

interface RepeatedTaxRecalculatorOptions<T> {
  taxDataField?: string;
  taxAmountField?: string;
  getTaxBase: (row: T) => number;
  getTaxRate?: (row: T) => number;
  roundValue?: (value: number) => number;
}

export const createRepeatedTaxRecalculator = <T extends Record<string, any>>({
  taxDataField = 'ma_thue_data',
  taxAmountField = 'thue_nt',
  getTaxBase,
  getTaxRate = row => Number(row?.[taxDataField]?.thue_suat ?? row?.[taxDataField]?.tax_rate ?? 0),
  roundValue = value => value
}: RepeatedTaxRecalculatorOptions<T>) => {
  return (updatedRows: T[], repeatedField: string, sourceRow: T) => {
    const repeatedBaseField = repeatedField.endsWith('_data') ? repeatedField.replace(/_data$/, '') : repeatedField;
    const taxBaseField = taxDataField.endsWith('_data') ? taxDataField.replace(/_data$/, '') : taxDataField;

    if (repeatedBaseField !== taxBaseField) {
      return updatedRows;
    }

    const sourceIndex = updatedRows.findIndex(row => row?.uuid === sourceRow?.uuid);
    if (sourceIndex === -1) return updatedRows;

    return updatedRows.map((row, index) => {
      if (index <= sourceIndex) return row;

      const taxBase = Number(getTaxBase(row) || 0);
      const taxRate = Number(getTaxRate(row) || 0);

      return {
        ...row,
        [taxAmountField]: roundValue((taxBase * taxRate) / 100)
      };
    });
  };
};

export const useRepeatRowsHandler = <T extends { uuid?: string | null }>({
  setRows,
  markDirty,
  columnDependencies = detailColumnDependencies,
  onAfterRepeat
}: UseRepeatRowsHandlerOptions<T>) => {
  return useCallback(
    ({ row, columnField }: RepeatRowsPayload<T>) => {
      if (!row?.uuid || !columnField) return;

      let didMutate = false;
      let sourceRow: T | null = null;

      setRows(prevRows => {
        if (!prevRows || prevRows.length === 0) return prevRows;

        const sourceIndex = prevRows.findIndex(prevRow => prevRow?.uuid === row.uuid);
        if (sourceIndex === -1 || sourceIndex === prevRows.length - 1) return prevRows;

        const foundSourceRow = prevRows[sourceIndex];
        if (!foundSourceRow) return prevRows;
        sourceRow = foundSourceRow;

        const normalizeField = (field: string) => (field?.endsWith('_data') ? field.replace(/_data$/, '') : field);
        const baseField = normalizeField(columnField);
        const dependencyFields = columnDependencies[columnField] ?? columnDependencies[baseField] ?? [];
        const candidateKeys = Object.keys(foundSourceRow as Record<string, unknown>);

        const keysToCopy = new Set<string>();
        const collectKeys = (field: string) => {
          const normalized = normalizeField(field);
          const relatedFields = new Set<string>([field]);

          if (normalized) {
            relatedFields.add(normalized);
            relatedFields.add(`${normalized}_data`);
          }

          relatedFields.forEach(relatedField => {
            candidateKeys.forEach(key => {
              if (['uuid', 'line', 'id'].includes(key)) return;

              const isExactField = key === relatedField;
              const isDataField = key === `${relatedField}_data` || key.startsWith(`${relatedField}_data.`);

              if (isExactField || isDataField) {
                keysToCopy.add(key);
              }
            });
          });
        };

        collectKeys(columnField);
        dependencyFields.forEach(collectKeys);

        if (keysToCopy.size === 0) return prevRows;

        let mutatedLocally = false;
        const updatedRows = prevRows.map((currentRow, index) => {
          if (index <= sourceIndex) return currentRow;

          mutatedLocally = true;

          const nextRow: Record<string, unknown> = { ...currentRow };
          keysToCopy.forEach(key => {
            nextRow[key] = cloneDeepValue((foundSourceRow as Record<string, unknown>)[key]);
          });

          return nextRow as T;
        });

        if (!mutatedLocally) return prevRows;

        didMutate = true;

        if (onAfterRepeat && sourceRow) {
          return onAfterRepeat(updatedRows, columnField, sourceRow);
        }

        return updatedRows;
      });

      if (didMutate) {
        markDirty?.();
      }
    },
    [setRows, markDirty, columnDependencies, onAfterRepeat]
  );
};

interface CopyCellValueOptions {
  columnField: string | null;
  columnValue: unknown;
  columnValues: Record<string, unknown>;
  fallback?: (text: string) => void;
}

export const copyCellValue = ({ columnField, columnValue, columnValues, fallback }: CopyCellValueOptions) => {
  const rawValue = columnValue ?? (columnField ? columnValues[columnField] : undefined);
  const textToCopy =
    rawValue === null || rawValue === undefined
      ? ''
      : typeof rawValue === 'object'
        ? JSON.stringify(rawValue)
        : String(rawValue);

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(textToCopy).catch(error => {
      console.error('Failed to copy cell value:', error);
    });
  } else {
    fallback?.(textToCopy);
  }
};
